import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from 'js/src/agent/skills/types';

export const elementMorphHandoffSkill: Skill = {
    name: SKILL_NAMES.elementMorphHandoff,
    kind: 'codeo',
    description:
        'Hand off between two on-screen states of the same scene by morphing one element into ' +
        'another at the same spot — a logo becomes a button, a wide card shrinks into an icon — so the eye tracks one continuous object.',
    instructions: `# Element Morph Handoff

Instead of cutting (or sweeping) between two on-screen states, let one element *become* the next, within the same scene. A hero lockup shrinks and a CTA pops in its place; a wide banner card morphs into a circular avatar. The viewer's eye locks onto the morphing object as the anchor through the handoff — far more premium than a slideshow cut, and unlike \`${SKILL_NAMES.momentSweepTransition}\` it ties two specific elements together rather than wiping the whole canvas.

Don't tween \`width\`/\`height\` for the size change — use uniform \`scale\` plus paint-only \`borderRadius\`/\`background\`/\`boxShadow\`. These morph the *apparent* shape without a layout tween; layout tweens reflow every frame and jitter under H.264 (same reasoning as \`${SKILL_NAMES.statBarsAndFills}\`).

## Markup

Both states share a grid cell so they land at the same center. The CTA starts at \`opacity: 0\` in CSS so it isn't visible before the swap.

\`\`\`html
<div style="position: absolute; inset: 0; display: grid; place-items: center;">
  <h1 id="hero-lockup"
      style="grid-area: 1/1; margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 96px; font-weight: 800; color: var(--l5-color-0, #101820);">Acme</h1>
  <div id="cta"
       style="grid-area: 1/1; opacity: 0; padding: 22px 44px; border-radius: 999px;
              background: var(--l5-color-1, #7a6dff); color: var(--l5-color-0, #ffffff);
              font-family: var(--l5-font-0), sans-serif; font-size: 32px;">Find out more</div>
</div>
\`\`\`

## Timeline code

Scale-swap — the simpler morph (no shape change, just a coordinated swap at one center):

\`\`\`js
const tl = gsap.timeline({ paused: true });
const SWAP = 2.6;
tl.to('#hero-lockup', { scale: 0.6, opacity: 0, duration: 0.4, ease: 'power2.in' }, SWAP);
tl.fromTo('#cta', { scale: 0.4, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.5, ease: 'back.out(2)' }, SWAP + 0.12);
window.__timelines = { main: tl };
\`\`\`

Card morph (variant) — a persistent container changes apparent shape while crossfading its own contents, added onto the same timeline. \`#morph-card-old\`/\`#morph-card-new\` are each their own text leaf with a unique \`id\`; give the "new" one \`opacity: 0\` in CSS so it starts hidden before the crossfade:

\`\`\`js
const MORPH_AT = 1.2, MORPH_DUR = 0.9;
// Shape change via scale + paint-only props (never width/height)
tl.to('#morph-card', { scale: 0.55, borderRadius: 999, background: 'var(--l5-color-1, #7a6dff)',
    duration: MORPH_DUR, ease: 'power2.inOut' }, MORPH_AT);
// Old content fades over the first ~40% of the morph
tl.to('#morph-card-old', { opacity: 0, duration: MORPH_DUR * 0.4, ease: 'power1.in' }, MORPH_AT);
// New content fades in over the last ~40%
tl.to('#morph-card-new', { opacity: 1, duration: MORPH_DUR * 0.4, ease: 'power1.out' },
    MORPH_AT + MORPH_DUR * 0.6);
\`\`\`

## Adapt

- **Hold a beat first** (0.6–1.5s) so the viewer registers state 1 before it morphs — an instant morph reads as a glitch.
- **Ease**: \`power2.inOut\` (balanced) or \`power3.inOut\` (snappier) for the shape change; \`back.out(2)\` only on the *entering* element of a scale-swap. Avoid overshoot on the morph itself — it fights the dimensional change.
- **Crossfade timing**: old content fades early, new content fades late, leaving a brief "shape-only" moment between — that gap is the natural blink the handoff hides behind.
- **Final handoff** (optional): tween the morph card's opacity to 0 over the last 0.1–0.2s to reveal a real next element in the same scene behind it — but only if that element is *pixel-identical* to the card's final state, or you get a visible pop. Use DOM order for stacking (render the anchor before the card), never snap \`z-index\` mid-fade.

## Pitfalls

- **No \`width\`/\`height\` tweens** — uniform \`scale\` for size, \`borderRadius\`/\`background\`/\`boxShadow\` for the shape/surface read.
- **\`overflow: hidden\` on the morph card** so its contents clip as the corner radius changes.
- Use \`background\` (not \`backgroundColor\`) when morphing to/from a gradient — GSAP interpolates gradients only when both states have the same number of stops.
- Both elements share one grid cell (\`grid-area: 1/1\`) so they morph at the same spot — offset centers make the swap look like a cut.
- The morph card and swapped elements are GSAP-tweened on \`scale\`/\`opacity\` — keep no CSS \`transform\` baked onto them; an entrance's from-vars (like \`#cta\`'s \`scale: 0.4\`) carry the resting start state instead.`,
};
